import React, { useContext, useEffect, useState } from "react";
import useUserAuth from "../../hooks/useUserAuth";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { API_PATHS } from "../../utils/apiPaths";
import axiosInstance from "../../utils/axiosInstance";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import moment from "moment";
import "moment/locale/fr";
import InfoCard from "../../components/Cards/InfoCard";
import { addThousandsSeparator } from "../../utils/helper";
import { LuArrowRight } from "react-icons/lu";
import TaskListTable from "../../components/TaskListTable";
import CustomPieChart from "../../components/Charts/CustomPieChart";
import CustomBarChart from "../../components/Charts/CustomBarChart";
import InfoCardPlus from "../../components/Cards/InfoCardPlus";

moment.locale("fr");

const COLORS = ["#8D51FF", "#00B8DB", "#7BCE00", "#e61526"];
const UserDashboard = () => {
  useUserAuth();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const [dashboardData, setDashboardData] = useState(null);
  const [pieChartData, setPieChartData] = useState([]);
  const [barChartData, setBarChartData] = useState([]);

  const prepareChartData = (data) => {
    const taskDistribution = data?.taskDistribution || {};
    const taskPriorityLevels = data?.priorityLevel || {};
    const taskDistributionData = [
      { status: "En attente", count: taskDistribution?.enattente || 0 },
      { status: "En cours", count: taskDistribution?.encours || 0 },
      { status: "Terminé", count: taskDistribution?.terminé || 0 },
      { status: "enretard", count: taskDistribution?.enretard || 0 },
    ];
    setPieChartData(taskDistributionData);

    const priorityLevelData = [
      { priority: "Faible", count: taskPriorityLevels?.faible || 0 },
      { priority: "Moyen", count: taskPriorityLevels?.moyen || 0 },
      { priority: "Élevé", count: taskPriorityLevels?.élevé || 0 },
    ];
    setBarChartData(priorityLevelData);
  };

  const getDashboardData = async () => {
    try {
      const response = await axiosInstance.get(
        API_PATHS.TASKS.GET_USER_DASHBOARD_DATA
      );
      console.log("response dashboard user", response.data);
      if (response.data) {
        setDashboardData(response.data);
        prepareChartData(response.data?.charts || {});
      }
      console.log("Données du tableau de bord :", response.data);
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des données du tableau de bord",
        error
      );
    }
  };

  const onSeeMore = () => navigate("/admin/manage-tasks");

  useEffect(() => {
    getDashboardData();
  }, []);

  if (!dashboardData) {
    return (
      <DashboardLayout activeMenu="Tableau de bord">
        <div role="status" class="max-w-sm animate-pulse">
          <div class="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-48 mb-4"></div>
          <div class="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px] mb-2.5"></div>
          <div class="h-2 bg-gray-200 rounded-full dark:bg-gray-700 mb-2.5"></div>
          <div class="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[330px] mb-2.5"></div>
          <div class="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[300px] mb-2.5"></div>
          <div class="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px]"></div>
          <span class="sr-only">Chargement...</span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout activeMenu="Tableau de bord">
      <div className="card my-5 mx-2 md:mx-5">
        <div>
          <h2 className="text-xl md:text-2xl">Bonjour {user?.name} 👋</h2>
          <p className="text-xs md:text-sm text-gray-400 mt-1.5">
            {moment().format("dddd, D MMMM YYYY")}
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mt-5">
          <InfoCard
            label="Total Tâches"
            value={addThousandsSeparator(
              dashboardData?.charts?.taskDistribution?.All || 0
            )}
            color="bg-blue-600"
          />
          <InfoCard
            label="Tâches en attente"
            value={addThousandsSeparator(
              dashboardData?.charts?.taskDistribution?.pendingTask || 0
            )}
            color="bg-violet-500"
          />
          <InfoCard
            label="Tâches en cours"
            value={addThousandsSeparator(
              dashboardData?.charts?.taskDistribution?.inProgressTask || 0
            )}
            color="bg-cyan-500"
          />
          <InfoCard
            label="Tâches terminées"
            value={addThousandsSeparator(
              dashboardData?.charts?.taskDistribution?.completedTask || 0
            )}
            color="bg-lime-500"
          />
          <InfoCardPlus
            label="Tâches enretard"
            value={addThousandsSeparator(
              dashboardData?.charts?.taskDistribution?.enretard || 0
            )}
            color="bg-red-500"
          />
        </div>
      </div>

      <div className="card">
        <div className="flex items-center justify-between">
          <h5 className="font-medium">Répartition des tâches</h5>
        </div>
        <CustomPieChart data={pieChartData} colors={COLORS} />
      </div>

      <div className="card mt-6">
        <div className="flex items-center justify-between">
          <h5 className="font-medium">Niveau de priorité des tâches</h5>
        </div>
        <CustomBarChart data={barChartData} colors={COLORS} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
        <div className="md:cols-span-2">
          <div className="card">
            <div className="flex items-center justify-between">
              <h5 className="text-large">Tâches récentes</h5>
              <button className="card-btn" onClick={onSeeMore}>
                Voir plus <LuArrowRight className="text-base" />
              </button>
            </div>
            <TaskListTable tableData={dashboardData?.recentTasks || []} />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default UserDashboard;
